/* eslint-disable no-unused-vars */
import { Route, Routes } from 'react-router';
import './App.css';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Layout from './components/Layout';
import Login from './Pages/Auth/Login/Login';
import Dashboard from './Pages/Dashboard';
import Tutorials from './Pages/Tutorials';
import ProtectedRoutes from './utils/PrivateRoutes';
import Courses from './Pages/Courses';
import Modules from './Pages/Modules';
import ModuleDetail from './Pages/ModuleDetail';
import Users from './Pages/Users';
import TutorialDetail from './Pages/TutorialDetail';
import ScholarshipGroups from './Pages/ScholarshipGroups';
import Cohorts from './Pages/Cohorts';
import SuperStudents from './Pages/SuperStudents';
import SuperStudentDetail from './Pages/SuperStudentDetail';
import CohortDetail from './Pages/CohortDetail';
import CohortPrefixes from './Pages/CohortPrefixes';
import ScholarshipGroupDetail from './Pages/ScholarshipGroupDetail';
import AssignmentDetail from './Pages/AssignmentDetail';
import QuizList from './Pages/QuizList';
import AddQuizQuestions from './Pages/AddQuizQuestions';
import RolesAndPermissions from './Pages/RolesAndPermissions';
import Events from './Pages/Events';
import Profile from './Pages/Profile';
import ResetPassword from './Pages/Auth/ResetPassword';
import ForgotPassword from './Pages/Auth/ForgotPassword';
import CohortQuizAnalytics from './Pages/CohortQuizAnalytics';
import Forums from './Pages/Forums';
import ForumDetail from './Pages/ForumDetail';
import LessonDetail from './Pages/LessonDetail';
import Email from './Pages/Email';
import PushNotifications from './Pages/PushNotifications';
import CohortLoginsAnalytics from './Pages/CohortLoginsAnalytics';
import ClassSessions from './Pages/ClassSessions';
import Notifications from './Pages/Notifications';
import SurveyList from './Pages/SurveyList';
import AddSurveyQuestions from './Pages/AddSurveyQuestions';
import { getNotifications } from './features/notification/notificationSlice';
import CohortModuleProgressAnalytics from './Pages/CohortModuleProgressAnalytics';
import PaidModulesStudentProgressAnalytics from './Pages/PaidModulesStudentProgressAnalytics';
import AddModule from './Pages/AddModule';
import PaidCohorts from './Pages/PaidCohorts';
import PaidCohortDetail from './Pages/PaidCohortDetail';
import CommunityTabs from './Pages/communityTabs';
import Tickets from './Pages/Tickets';
import TicketDetail from './Pages/TicketDetail';
import AnalyticsDashBoard from './Pages/Analytics';
import PaidModulesAnalytics from './Pages/PaidModulesAnalytics';
import CohortGroupMembers from './Pages/CohortGroupMembers';
import AdminRoute from './utils/AdminRoute';
import PermissionRoute from './utils/PermissionRoute';
import StudentProfileRedirect from './utils/StudentProfileRedirect';
import {
  PERMISSION_MANAGE_ADMISSIONS,
  PERMISSION_MANAGE_FORMS,
  PERMISSION_ADD_JOBS,
  PERMISSION_MANAGE_JOB_DIGEST,
  PERMISSION_MANAGE_SUPER_STUDENTS,
  PERMISSION_SEND_SMS_CAMPAIGN,
  PERMISSION_VIEW_STUDENT_INTELLIGENCE,
} from './utils/permissionKeys';
import CreateLesson from './Pages/CreateLesson';
import CreateWeekAssignment from './Pages/CreateWeekAssignment';
import CohortAssignmentAnalytics from './Pages/CohortAssignmentAnalytics';
import CohortCertificationAnalytics from './Pages/CohortCertificationAnalytics';
import SurveyAnalytics from './Pages/CohortSurveyAnalytics';
import ClassSessionAnalytics from './Pages/ClassSessionAnalytics';
import OnboardingAutomation from './Pages/OnboardingAutomation';
import HPFAssessment from './Pages/HPFAssessment/HPFAssessment';
import HPFAssessmentDetail from './Pages/HPFAssessment/HPFAssessmentDetail';
import StudentIntelligence from './Pages/StudentIntelligence';
import AdminLoginsAnalytics from './Pages/AdminLoginsAnalytics';
import SmsCampaigns from './Pages/SmsCampaigns';
import SmsComposer from './Pages/SmsComposer';
import SmsCampaignDetail from './Pages/SmsCampaignDetail';
import ImpactReport from './Pages/ImpactReport';
import ReportPartners from './Pages/ReportPartners';
import JobDigest from './Pages/JobDigest';
import Admissions from './Pages/Admissions';
import SpecializationQualification from './Pages/SpecializationQualification';
import CohortReminders from './Pages/CohortReminders';
import MyInvoices from './Pages/MyInvoices';
import FormAssignments from './Pages/FormAssignments';
import FormAssignmentDetail from './Pages/FormAssignmentDetail';
import FinanceInvoices from './Pages/FinanceInvoices';
import SelfPacedAnalytics from './Pages/SelfPacedAnalytics';
import { publicEnv } from './publicEnv';

const notificationSocketUrl =
  publicEnv('REACT_APP_NOTIFICATION_SOCKET_URL') ||
  'wss://api.lms.v2.powerlearnprojectafrica.org/notification/socket';

function App() {
  const [isMobileView, setIsMobileView] = useState(false);
  const dispatch = useDispatch();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [notifications, setNotifications] = useState({
    count: 0,
    messages: [],
  });

  useEffect(() => {
    if (token) {
      const socketConnection = new WebSocket(
        `${notificationSocketUrl}/notification?Authorization=${token}`
      );

      socketConnection.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setNotifications({
          count: data?.length || 0,
          messages: data || [],
        });
        dispatch(
          getNotifications({
            count: data?.length || 0,
            messages: data || [],
          })
        );
      };

      socketConnection.onerror = () => {
        // Error handling without console logs
      };

      return () => {
        socketConnection.close();
      };
    }
    return undefined;
  }, [dispatch, token]);

  const handleResize = useCallback(() => {
    if (window.innerWidth <= 900) {
      setIsMobileView(true);
    } else {
      setIsMobileView(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return isMobileView ? (
    <div className="flex justify-center items-center h-[100vh] max-h-[100vh] align-middle">
      <h1 className="font-medium text-xl text-center">
        This application is only available for desktop devices
      </h1>
    </div>
  ) : (
    <Routes>
      <Route path="/*" element={<ProtectedRoutes />}>
        <Route path="/*" element={<Layout />}>
          <Route path="" element={<Dashboard />} />
          <Route path="courses" element={<Courses />} />
          <Route path="analytics" element={<AnalyticsDashBoard />} />
          <Route path="analytics/self-paced" element={<SelfPacedAnalytics />} />
          <Route path="impact" element={<ImpactReport />} />
          <Route path="impact/partners" element={<ReportPartners />} />
          <Route
            path="analytics/cohort-quiz-analytics"
            element={<CohortQuizAnalytics />}
          />
          <Route
            path="analytics/cohort-assignment-analytics"
            element={<CohortAssignmentAnalytics />}
          />
          <Route
            path="analytics/cohort-module-progress-analytics"
            element={<CohortModuleProgressAnalytics />}
          />
          <Route
            path="analytics/cohort-logins-analytics"
            element={<CohortLoginsAnalytics />}
          />
          <Route
            path="analytics/survey-analytics"
            element={<SurveyAnalytics />}
          />
          <Route
            path="analytics/cohort-certification-analytics"
            element={<CohortCertificationAnalytics />}
          />
          <Route
            path="analytics/paid-modules"
            element={<PaidModulesAnalytics />}
          />
          <Route
            path="analytics/cohort-group-members"
            element={<CohortGroupMembers />}
          />
          <Route
            path="analytics/paid-modules-students-progress"
            element={<PaidModulesStudentProgressAnalytics />}
          />
          <Route path="courses/:courseId" element={<Modules />} />
          <Route path="courses/:courseId/addModule" element={<AddModule />} />
          <Route path="module/:moduleId" element={<ModuleDetail />} />
          <Route path="tutorials" element={<Tutorials />} />
          <Route path="tutorials/:tutorialId" element={<TutorialDetail />} />
          <Route path="groups" element={<ScholarshipGroups />} />
          <Route path="community" element={<CommunityTabs />} />
          <Route
            path="groups/:groupName/:groupId"
            element={<ScholarshipGroupDetail />}
          />
          <Route
            path="courses/modules/week-:weekName/:weekId/quizzes"
            element={<QuizList />}
          />
          <Route
            path="courses/modules/weeks/quiz-:quizName/:quizId/questions"
            element={<AddQuizQuestions />}
          />
          <Route
            path="courses/modules/week-:weekName/:weekId/surveys"
            element={<SurveyList />}
          />
          <Route
            path="courses/modules/weeks/survey-:surveyName/:surveyId/questions"
            element={<AddSurveyQuestions />}
          />
          <Route
            path="courses/modules/week-:weekName/:weekId/lessons/create"
            element={<CreateLesson />}
          />
          <Route
            path="courses/modules/week-:weekName/:weekId/assignments/create"
            element={<CreateWeekAssignment />}
          />
          <Route
            path="courses/modules/:moduleId/week/:weekId/lesson/:lessonId"
            element={<LessonDetail />}
          />
          <Route
            path="group/:groupId/assignment/:assignmentId"
            element={<AssignmentDetail />}
          />
          <Route
            path="assignment/:assignmentId"
            element={<AssignmentDetail weekly />}
          />
          <Route path="cohorts" element={<Cohorts />} />
          <Route path="cohorts/prefixes" element={<CohortPrefixes />} />
          <Route
            path="cohorts=:cohortStatus/:cohortName/:cohortId"
            element={<CohortDetail />}
          />
          <Route
            path="cohorts=:cohortStatus/:cohortName/:cohortId/specialization"
            element={<SpecializationQualification />}
          />
          <Route path="my-invoices" element={<MyInvoices />} />
          <Route path="finance/invoices" element={<FinanceInvoices />} />
          <Route
            path="cohorts=:cohortStatus/:cohortName/:cohortId/reminders"
            element={<CohortReminders />}
          />
          <Route
            element={
              <PermissionRoute permission={PERMISSION_MANAGE_SUPER_STUDENTS} />
            }
          >
            <Route path="super-students" element={<SuperStudents />} />
            <Route
              path="super-students/:studentId"
              element={<SuperStudentDetail />}
            />
          </Route>
          <Route path="paid-cohorts" element={<PaidCohorts />} />
          <Route
            path="paid-cohorts=:paidCohortStatus/:paidCohortName/:paidCohortId"
            element={<PaidCohortDetail />}
          />
          <Route path="notifications" element={<Notifications />} />
          <Route
            path="users/student/:studentId"
            element={<StudentProfileRedirect />}
          />
          <Route
            element={
              <PermissionRoute
                permission={PERMISSION_VIEW_STUDENT_INTELLIGENCE}
              />
            }
          >
            <Route
              path="student-intelligence"
              element={<StudentIntelligence />}
            />
          </Route>
          <Route element={<AdminRoute />}>
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<Profile />} />
            <Route path="roles&permissions" element={<RolesAndPermissions />} />
            <Route
              path="onboarding-automation"
              element={<OnboardingAutomation />}
            />
            <Route
              path="analytics/admin-logins"
              element={<AdminLoginsAnalytics />}
            />
          </Route>
          <Route path="events" element={<Events />} />
          <Route path="emails" element={<Email />} />
          <Route
            element={
              <PermissionRoute permission={PERMISSION_SEND_SMS_CAMPAIGN} />
            }
          >
            <Route path="sms" element={<SmsCampaigns />} />
            <Route path="sms/new" element={<SmsComposer />} />
            <Route path="sms/:campaignId" element={<SmsCampaignDetail />} />
          </Route>
          <Route
            element={
              <PermissionRoute
                permission={[PERMISSION_MANAGE_JOB_DIGEST, PERMISSION_ADD_JOBS]}
              />
            }
          >
            <Route path="jobs" element={<JobDigest />} />
          </Route>
          <Route
            element={
              <PermissionRoute permission={PERMISSION_MANAGE_ADMISSIONS} />
            }
          >
            <Route path="admissions" element={<Admissions />} />
          </Route>
          <Route
            element={<PermissionRoute permission={PERMISSION_MANAGE_FORMS} />}
          >
            <Route path="forms" element={<FormAssignments />} />
            <Route
              path="forms/:assignmentId"
              element={<FormAssignmentDetail />}
            />
          </Route>
          <Route path="push-notifications" element={<PushNotifications />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="tickets/:ticketid" element={<TicketDetail />} />
          <Route path="forums" element={<Forums />} />
          <Route path="class-sessions" element={<ClassSessions />} />
          <Route
            path="class-sessions/:classSessionId/:zoomSessionType/:zoomSessionID"
            element={<ClassSessionAnalytics />}
          />
          <Route path="forums/:forumId" element={<ForumDetail />} />
          <Route path="hpf-assessment" element={<HPFAssessment />} />
          <Route path="hpf-assessment/:id" element={<HPFAssessmentDetail />} />
        </Route>
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password/" element={<ResetPassword />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
    </Routes>
  );
}

export default App;
